import { Button } from "@/components/ui/button";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useCreateProject } from "@/lib/hooks/useProjects";
import { ArrowUpDown, FolderPlus, Search } from "lucide-react";

type SortOption = "modified" | "created" | "name";

interface ProjectsHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  projectCount: number;
  totalCount: number;
}

const sortOptions = [
  { value: "modified" as const, label: "Recently Modified" },
  // { value: "created" as const, label: "Recently Created" },
  { value: "name" as const, label: "Name" },
];

export function ProjectsHeader({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  projectCount,
  totalCount,
}: ProjectsHeaderProps) {
  const createProject = useCreateProject();

  const handleCreateProject = () => {
    createProject.mutate({});
  };

  const currentSortLabel =
    sortOptions.find((o) => o.value === sortBy)?.label ?? "Sort";

  return (
    <div className="space-y-6 @container">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Projects</h1>
        </div>

        <Button
          onClick={handleCreateProject}
          disabled={createProject.isPending}
          className="gap-2"
        >
          <FolderPlus className="w-4 h-4" />
          New Project
        </Button>
      </div>

      <div className="grid grid-cols-1 @md:grid-cols-2 @lg:grid-cols-[auto_1fr_auto] gap-4 justify-between">
        <div className="relative @md:col-span-2 @lg:col-span-1">
          <Search className="absolute left-3 top-1/2 -mt-2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-muted-foreground mt-1 text-center flex items-center justify-center">
          {projectCount === totalCount
            ? `${totalCount} ${totalCount === 1 ? "project" : "projects"}`
            : `${projectCount} of ${totalCount} projects`}
        </div>

        <div className="@lg:justify-self-end">
          <FpDropdownMenu>
            <FpDropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <ArrowUpDown className="w-4 h-4" />
                {currentSortLabel}
              </Button>
            </FpDropdownMenuTrigger>
            <FpDropdownMenuContent align="end">
              {sortOptions.map((option) => (
                <FpDropdownMenuItem
                  key={option.value}
                  onClick={() => onSortChange(option.value)}
                  className="hover:bg-secondary hover:text-secondary-foreground focus:bg-secondary focus:text-secondary-foreground cursor-pointer"
                >
                  {option.label}
                </FpDropdownMenuItem>
              ))}
            </FpDropdownMenuContent>
          </FpDropdownMenu>
        </div>
      </div>
    </div>
  );
}
